import { deleteFollow } from '@db/Requests';
import { addUser, addUserPrivateDoc, updateUser } from '@db/Users';
import { addBlockedUser, deleteBlockedUser } from '@db/Users/Block';
import { isUserExists, isUserPrivateExists } from '@db/Users/Check';
import { getGroupMembers, getMostFollowedUsers, getUser, getUsersIn } from '@db/Users/Get';
import { getErrorProps } from '@helpers/Errors';
import { sortByDisplayName } from '@helpers/Formatters';
import {
  AppQueryCache,
  AppQueryDomain,
  AppQueryKeySegment,
  createQueryIdListKeyPart,
  createQueryKey,
  useAppMutation,
  useAppQuery,
  useAppQueryCache,
} from '@helpers/query';
import type { Photo, Place, User, UserFormData, UserPrivateFormData } from '@types';

const EMPTY_USERS: User[] = [];

export type UseUserQueryOptions = {
  /** Set false while route params or parent data are not ready. */
  enabled?: boolean;
  userId?: string;
};

export type UseUsersQueryOptions = {
  /** Set false while route params or parent data are not ready. */
  enabled?: boolean;
  /** User ids loaded as a sorted user list. */
  userIds: readonly string[];
};

export type UseGroupMembersQueryOptions = {
  /** Set false while route params are not ready. */
  enabled?: boolean;
  groupId?: string;
};

export type UseMostFollowedUsersQueryOptions = {
  /** Set false while auth or parent context is not ready. */
  enabled?: boolean;
};

export type UseUserOnboardingStatusQueryOptions = {
  /** Set false while auth is not ready. */
  enabled?: boolean;
  userId?: string;
};

export type UserOnboardingStatus = {
  onboarded: boolean;
  userDocExists: boolean;
};

export type CreateUserMutationVariables = {
  /** Public profile fields submitted during onboarding. */
  user: Pick<UserFormData, 'displayName' | 'hometown' | 'public' | 'username'>;
  /** Optional local profile photo selected by the user. */
  profilePhoto?: Photo | null;
};

export type UpdateUserMutationVariables = {
  /** User's display name. */
  displayName: string;
  /** User's selected hometown. */
  hometown: Place;
  /** Whether the user's profile should be public. */
  isPublic: boolean;
  /** Optional local profile photo selected by the user. */
  profilePhoto?: Photo | null;
  /** User's selected username. */
  username: string;
};

export type CreateUserPrivateMutationVariables = {
  /** Private profile fields submitted during onboarding. */
  userPrivate: UserPrivateFormData;
};

export enum BlockUserMutationAction {
  BLOCK = 'block',
  UNBLOCK = 'unblock',
}

export type BlockUserMutationVariables = {
  /** Whether the user should be blocked or unblocked. */
  action: BlockUserMutationAction;
  /** User being blocked or unblocked. */
  userId: string;
};

export function useUserQuery({ enabled = true, userId }: UseUserQueryOptions) {
  const isEnabled = enabled && Boolean(userId);
  const query = useAppQuery<User>({
    queryKey: createQueryKey(AppQueryDomain.USERS, AppQueryKeySegment.DETAIL, userId ?? null),
    queryFn: () => getUser(userId ?? ''),
    enabled: isEnabled,
    retry: false,
  });

  return {
    loading: isEnabled && query.isLoading,
    user: isEnabled ? (query.data ?? null) : null,
  };
}

export function useUserOnboardingStatusQuery({
  enabled = true,
  userId,
}: UseUserOnboardingStatusQueryOptions) {
  const isEnabled = enabled && Boolean(userId);
  const query = useAppQuery<UserOnboardingStatus>({
    queryKey: createQueryKey(
      AppQueryDomain.USERS,
      AppQueryKeySegment.ONBOARDING_STATUS,
      userId ?? null
    ),
    queryFn: () => getUserOnboardingStatus(userId ?? ''),
    enabled: isEnabled,
    retry: false,
  });

  return {
    error: query.isLoadingError,
    errorProps:
      query.error && query.isLoadingError
        ? getErrorProps(query.error, { onRetry: query.refetch })
        : undefined,
    loading: isEnabled && query.isLoading,
    onboarded: isEnabled ? Boolean(query.data?.onboarded) : false,
    userDocExists: isEnabled ? Boolean(query.data?.userDocExists) : false,
  };
}

export function useMostFollowedUsersQuery({
  enabled = true,
}: UseMostFollowedUsersQueryOptions = {}) {
  const query = useAppQuery<User[]>({
    queryKey: createQueryKey(AppQueryDomain.USERS, AppQueryKeySegment.MOST_FOLLOWED),
    queryFn: getMostFollowedUsers,
    enabled,
    placeholderData: [],
    retry: false,
  });

  return {
    loading: enabled && query.isLoading,
    users: enabled ? (query.data ?? EMPTY_USERS) : EMPTY_USERS,
  };
}

export function useUsersQuery({ enabled = true, userIds }: UseUsersQueryOptions) {
  const normalizedUserIds = createQueryIdListKeyPart(userIds);
  const isEnabled = enabled && normalizedUserIds.length > 0;
  const query = useAppQuery<User[]>({
    queryKey: createQueryKey(AppQueryDomain.USERS, AppQueryKeySegment.LIST, normalizedUserIds),
    queryFn: async () => sortByDisplayName(await getUsersIn([...normalizedUserIds])),
    enabled: isEnabled,
    retry: false,
  });

  return {
    error: query.isLoadingError,
    errorProps:
      query.error && query.isLoadingError
        ? getErrorProps(query.error, { onRetry: query.refetch })
        : undefined,
    loading: isEnabled && query.isLoading,
    reload: async () => {
      if (!isEnabled) return;

      await query.refetch();
    },
    users: isEnabled ? (query.data ?? EMPTY_USERS) : EMPTY_USERS,
  };
}

export function useGroupMembersQuery({ enabled = true, groupId }: UseGroupMembersQueryOptions) {
  const isEnabled = enabled && Boolean(groupId);
  const query = useAppQuery<User[]>({
    queryKey: createQueryKey(AppQueryDomain.GROUPS, AppQueryKeySegment.MEMBERS, groupId ?? null),
    queryFn: async () => sortByDisplayName(await getGroupMembers(groupId ?? '')),
    enabled: isEnabled,
    retry: false,
  });

  return {
    error: query.isLoadingError,
    errorProps:
      query.error && query.isLoadingError
        ? getErrorProps(query.error, { onRetry: query.refetch })
        : undefined,
    loading: isEnabled && query.isLoading,
    members: isEnabled ? (query.data ?? EMPTY_USERS) : EMPTY_USERS,
  };
}

export function useCreateUserMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, CreateUserMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.USERS, AppQueryKeySegment.CREATE_MUTATION),
    mutationFn: ({ profilePhoto, user }) => addUser(user, profilePhoto ?? undefined),
    onSuccess: async () => {
      await invalidateUserMutationQueries(queryCache);
    },
  });
}

export function useUpdateUserMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, UpdateUserMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.USERS, AppQueryKeySegment.UPDATE_MUTATION),
    mutationFn: ({ displayName, hometown, isPublic, profilePhoto, username }) =>
      updateUser(displayName, username, hometown, isPublic, profilePhoto ?? undefined),
    onSuccess: async () => {
      await invalidateUserMutationQueries(queryCache);
    },
  });
}

export function useCreateUserPrivateMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, CreateUserPrivateMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.USERS, AppQueryKeySegment.PRIVATE_CREATE_MUTATION),
    mutationFn: ({ userPrivate }) => addUserPrivateDoc(userPrivate),
    onSuccess: async () => {
      await invalidateUserMutationQueries(queryCache);
    },
  });
}

export function useBlockUserMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, BlockUserMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.USERS, AppQueryKeySegment.BLOCK_MUTATION),
    mutationFn: async ({ action, userId }) => {
      if (action === BlockUserMutationAction.UNBLOCK) {
        await deleteBlockedUser(userId);
        return;
      }

      await deleteFollow(userId);
      await addBlockedUser(userId);
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateUserMutationQueries(queryCache),
        queryCache.invalidateQueries(createQueryKey(AppQueryDomain.FOLLOWS)),
      ]);
    },
  });
}

async function getUserOnboardingStatus(userId: string): Promise<UserOnboardingStatus> {
  const [userDocExists, userPrivateDocExists] = await Promise.all([
    isUserExists(userId),
    isUserPrivateExists(userId),
  ]);

  return {
    onboarded: userDocExists && userPrivateDocExists,
    userDocExists,
  };
}

async function invalidateUserMutationQueries(queryCache: AppQueryCache) {
  await Promise.all([
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.USERS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.PLANS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.GROUPS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.EXPLORER)),
  ]);
}
